import { useEffect, useState, useMemo } from "react";
import {
  Row,
  Col,
  Button,
  Container,
  InputGroup,
  FormControl,
  DropdownButton,
  Dropdown,
} from "react-bootstrap";
import DynamicTable from "../components/DynamicTable";
import Heading from "../components/Heading";
import Loading from "../components/Loading";
import Error from "../components/Error";
import ExportDocument from "../components/ExportDocument";
import DateRangePicker from "../components/DateRangePicker";
import axiosInstance from "../api/axiosConfig";
import { constants } from "../constants/index";
import { refreshButton } from "../util/RefreshButton";
import { formatDateOnly } from "../util/dateUtil";
import { getDocumentDisplayStatus } from "../util/documentStatus";

const getStoredFilters = () => {
  const storedValue = localStorage.getItem("documentListFilters");

  if (!storedValue) {
    return null;
  }

  try {
    return JSON.parse(storedValue);
  } catch {
    return null;
  }
};

const getDefaultStartDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return date;
};

const getDefaultEndDate = () => new Date();

const parseStoredDate = (value, fallback) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  if (typeof value === "number") {
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? fallback : parsedDate;
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? fallback : parsedDate;
};

const DocumentList = () => {
  const savedFilters = getStoredFilters();
  const [selectedItem, setSelectedItem] = useState("By Type");
  const [documentData, setDocumentData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(() => {
    return savedFilters?.searchQuery || "";
  });

  const [sortOrder, setSortOrder] = useState(() => {
    return savedFilters?.sortOrder || "asc";
  });

  const [startDate, setStartDate] = useState(() => {
    return parseStoredDate(savedFilters?.startDate, getDefaultStartDate());
  });

  const [endDate, setEndDate] = useState(() => {
    return parseStoredDate(savedFilters?.endDate, getDefaultEndDate());
  });

  const [selectedStatus, setSelectedStatus] = useState(() => {
    return savedFilters?.selectedStatus || "All";
  });

  const fetchData = async (payload = {}) => {
    setLoading(true);

    try {
      const response = await axiosInstance.post("/s3_file_display", payload);
      if (Array.isArray(response.data?.data)) {
        setDocumentData(response.data.data);
        setError(null);
      } else if (response.data.data?.message) {
        setDocumentData([]);
        setError(response.data);
      }
      setLoading(false);
    } catch (error) {
      setError(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    const filters = {
      searchQuery,
      selectedStatus,
      startDate: startDate ? startDate.getTime() : null,
      endDate: endDate ? endDate.getTime() : null,
      sortOrder,
    };
    localStorage.setItem("documentListFilters", JSON.stringify(filters));
  }, [searchQuery, selectedStatus, startDate, endDate, sortOrder]);

  useEffect(() => {
    if (startDate && endDate) {
      fetchData({
        action: "get_files",
        start_date: formatDateOnly(startDate),
        end_date: formatDateOnly(endDate),
      });
    } else if (!startDate && !endDate) {
      fetchData({ action: "get_files" });
    }
  }, [startDate, endDate]);

  const filteredItems = useMemo(() => {
    const filtered = documentData.filter((item) => {
      const displayStatus = getDocumentDisplayStatus(item);
      const matchesQuery =
        item?.file_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        displayStatus.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.file_type &&
          item.file_type.toLowerCase().includes(searchQuery.toLowerCase()) &&
          item.file_type !== null);

      // const itemDate = new Date(item.processed_at);
      // const start = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : null;
      // const end = endDate ? new Date(endDate).setHours(23, 59, 59, 999) : null;

      // const matchesDateRange =
      //   (!start || itemDate >= start) &&
      //   (!end || itemDate <= end);

      // const matchesDateRange =
      //   (!startDate || new Date(item.processed_at) >= new Date(startDate)) &&
      //   (!endDate || new Date(item.processed_at) <= new Date(endDate));
      
      const itemStatus = displayStatus.toLowerCase().trim();

      let matchesStatus = true;
      if (selectedStatus === "Reviewed") {
        matchesStatus = itemStatus === "reviewed";
      } else if (selectedStatus === "Verified") {
        matchesStatus = itemStatus === "verified";
      } else if (selectedStatus !== "All") {
        matchesStatus = itemStatus === selectedStatus.toLowerCase();
      }

      // const matchesStatus =
      // selectedStatus === "All" || item.status === selectedStatus;

      // const matchesFailStatus =
      //     item.status === "Fail" && item.is_reviewed === "N";

      // console.log(selectedStatus,  item.is_reviewed);

      // const matchesReviewed = selectedStatus === "Reviewed" ? item.is_reviewed === "Y" : true;

      return matchesQuery && matchesStatus;
    });

    // Sorting logic
    return filtered.sort((a, b) => {
      const dateA = new Date(a.processed_at);
      const dateB = new Date(b.processed_at);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
  }, [documentData, searchQuery, sortOrder, selectedStatus]);

  if (loading) {
    return <Loading />;
  }

  // if (error) {
  //   return <Error message={error.message} />;
  // }

  return (
    <>
      <Heading title={constants.DOCUMENTS} />
      <Container className="main">
        <Row className="d-flex flex-row p-2 mt-5">
          <Col>
            <h4 className="ps-2">{constants.STATUS_UPDATE}</h4>
          </Col>
          <Col xs="auto" className="d-flex flex-row justify-content-end">
            <Row>
              <Col xs="auto">
                <InputGroup className="mb-3">
                  <span className="input-group-text search-bg-color">
                    <i className="fas fa-search"></i>
                  </span>
                  <FormControl
                    className="custom-outline-input custom-placeholder-input no-outline"
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <ExportDocument
                selectedItem={selectedItem}
                setSelectedItem={setSelectedItem}
                data={filteredItems}
              />
              <Col xs="auto">
                <Button
                  onClick={refreshButton}
                  className="btn-outline-steel-blue btn-light me-2 dropdown-custom-color"
                >
                  <i className="fa fa-sync" aria-hidden="true"></i>&nbsp;Refresh
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>

        <Row className="mb-5  flex-wrap px-3">
          <Col>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
            />
          </Col>

          <Col xs="auto">
            <DropdownButton
              id="dropdown-status-button"
              title={`By Status (${selectedStatus})`}
              onSelect={(e) => setSelectedStatus(e)}
              variant="outline-primary"
              className="btn-light dropdown-custom-color"
            >
              <Dropdown.Item key="all" eventKey="All">All</Dropdown.Item>
              <Dropdown.Item key="success" eventKey="Success">Success</Dropdown.Item>
              <Dropdown.Item key="fail" eventKey="Fail">Fail</Dropdown.Item>
              <Dropdown.Item key="partial-success" eventKey="Partial Success">
                Partial Success
              </Dropdown.Item>
              <Dropdown.Item key="reviewed" eventKey="Reviewed">Reviewed</Dropdown.Item>
              <Dropdown.Item key="verified" eventKey="Verified">Verified</Dropdown.Item>
            </DropdownButton>
          </Col>

          <Col xs="auto">
            <DropdownButton
              id="dropdown-basic-button"
              title={`Sort by Date (${sortOrder === "asc" ? "Asc" : "Desc"})`}
              onSelect={(e) => setSortOrder(e)}
              variant="outline-primary"
              className="btn-light dropdown-custom-color"
            >
              <Dropdown.Item key="asc" eventKey="asc">Ascending</Dropdown.Item>
              <Dropdown.Item key="desc" eventKey="desc">Descending</Dropdown.Item>
            </DropdownButton>
          </Col>
        </Row>

        {error ? (
          <Row>
            <Col className="d-flex justify-content-center align-items-center">
              <Error
                message={error.message ? error.message : error.data.message}
              />
            </Col>
          </Row>
        ) : (
          <DynamicTable columns={constants.doc_columns} data={filteredItems} />
        )}
      </Container>
    </>
  );
};

export default DocumentList;
